import React, { useState, useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import CTAButton from "../cta-button/cta-button";
import { StyledSection, PackageSectionHeadline, PackageSectionDescription, HorizontalScrollWrapper, PackageButtonWrapper } from "./package-section.styles";
import useIsMobile from "../../hooks/useIsMobile";
import PackageCard from "../../routes/landing-page/sub-pages/details-page/components/package-card/package-card";
import PackageBenefitsModal from "../../modals/package-benefits-modal/package-benefits-modal";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

interface PackageSectionProps {
    id?: string | undefined;
    hideComparePackages?: boolean;
    withoutBackground?: boolean;
}

const getBasicFeatures = (t: TFunction): string[] => [
    t('packages.features.handover_support'),
    t('packages.features.marketing_rental'),
    t('packages.features.tenant_management'),
    t('packages.features.maintenance'),
    t('packages.features.landlord_portal'),
];

const getPremiumFeatures = (t: TFunction): string[] => [
    t('packages.features.handover_support'),
    t('packages.features.marketing_rental'),
    t('packages.features.tenant_management'),
    t('packages.features.maintenance'),
    t('packages.features.landlord_portal'),
    t('packages.features.service_charge_management'),
    t('packages.features.debt_collection'),
    t('packages.features.warranty_management'),
];

const PackageSection: React.FC<PackageSectionProps> = ({ id, hideComparePackages = false, withoutBackground = false }) => {
    const isMobile = useIsMobile();
    const { t } = useTranslation();

    // Memoize features to update when language changes
    const basicFeatures = useMemo(() => getBasicFeatures(t), [t]);
    const premiumFeatures = useMemo(() => getPremiumFeatures(t), [t]);

    // States
    const [isPackageBenefitsVisible, setIsPackageBenefitsModalVisible] = useState(false);

    return (
        <>
            <StyledSection id={id} withoutBackground={withoutBackground}>
                <PackageSectionHeadline>
                    {isMobile
                        ? t('packages.headline_mobile')
                        : t('packages.headline_desktop')}
                </PackageSectionHeadline>
                <PackageSectionDescription>
                    {t('packages.description')}
                </PackageSectionDescription>

                {isMobile ? (
                    <HorizontalScrollWrapper>
                        <PackageCard
                            title="Premium"
                            price={7}
                            features={premiumFeatures}
                            isPremium={true}
                        />
                        <PackageCard
                            title="Basic"
                            price={5}
                            features={basicFeatures}
                            isPremium={false}
                        />
                    </HorizontalScrollWrapper>
                ) : (
                    <Row className="justify-content-center">
                        <Col xs={12} md={6} xl={4}>
                            <PackageCard
                                title="Basic"
                                price={5}
                                features={basicFeatures}
                                isPremium={false}
                            />
                        </Col>

                        <Col xs={12} md={6} xl={4}>
                            <PackageCard
                                title="Premium"
                                price={7}
                                features={premiumFeatures}
                                isPremium={true}
                            />
                        </Col>
                    </Row>
                )}

                {!hideComparePackages && (
                    <PackageButtonWrapper>
                                                    <CTAButton
                                variant="primary"
                                size="L"
                                label={t('packages.compare_button')}
                                onClick={() => setIsPackageBenefitsModalVisible(true)}
                                minWidth={240}
                            />
                    </PackageButtonWrapper>
                )}
            </StyledSection>

            <PackageBenefitsModal
                isVisible={isPackageBenefitsVisible}
                onClose={() => setIsPackageBenefitsModalVisible(false)}
            />
        </>
    );
};

export default PackageSection;
